// My open missed calls for the nav badge (owner request, PJ, 2026-10-24: the Missed calls page). GET
// /api/inbound/missed-calls/count every 30 s while the app is open and the tab is visible (and at once when it becomes
// visible again), the same pattern as the Texts badge (web/src/texts.ts). The page refreshes it after Mark done and on
// load. The ringing pop-up for a live call is unchanged (web/src/phone/PhoneProvider.tsx).
import { useEffect, useState } from 'preact/hooks';
import { api } from './api';
import { MISSED_POLL_MS, type MissedCallCount } from '../../src/shared/api-missed';

let current: MissedCallCount | null = null;
const subs = new Set<(s: MissedCallCount | null) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let inflight: Promise<MissedCallCount | null> | null = null;
let onVisible: (() => void) | null = null;

export function refreshMissed(): Promise<MissedCallCount | null> {
  if (inflight) return inflight;
  inflight = api.get<MissedCallCount>('/api/inbound/missed-calls/count')
    .then((next) => { current = next; for (const f of subs) f(next); return next; })
    .catch(() => current)
    .finally(() => { inflight = null; });
  return inflight;
}

/** The page already counted them (its list answer): no second request. */
export function setMissedOpen(open: number): void {
  current = { open };
  for (const f of subs) f(current);
}

/** Sign-out: forget the count and stop polling. */
export function resetMissed(): void {
  current = null;
  if (timer) { clearInterval(timer); timer = null; }
  if (onVisible) { document.removeEventListener('visibilitychange', onVisible); onVisible = null; }
  for (const f of subs) f(null);
}

export function useMissedCount(): MissedCallCount | null {
  const [s, setS] = useState<MissedCallCount | null>(current);
  useEffect(() => {
    subs.add(setS);
    if (!current) void refreshMissed();
    if (!timer) {
      timer = setInterval(() => { if (subs.size && document.visibilityState !== 'hidden') void refreshMissed(); }, MISSED_POLL_MS);
      onVisible = () => { if (document.visibilityState === 'visible' && subs.size) void refreshMissed(); };
      document.addEventListener('visibilitychange', onVisible);
    }
    return () => { subs.delete(setS); };
  }, []);
  return s;
}
